import React, { useEffect } from "react";
import { Link, useLocation } from "wouter";
import { ArrowLeft } from "lucide-react";
const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="bg-[#f6fafa] h-[100vh] mb-2 py-10 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-[#2e5b5c] text-white px-2 py-5">
          <div className="flex">
            <div>
              {" "}
              <Link
                href="/"
                className="text-white hover:underline flex items-center"
              >
                <span className="text-lg mr-1">
                  <ArrowLeft />{" "}
                </span>
              </Link>
            </div>
            <div>
              {" "}
              <h1 className="text-xl font-bold">
                Privacy Policy for InMyNameKYC
              </h1>
            </div>
          </div>
          <p className="text-sm mt-1 px-7">Effective Date: 3rd July 2025</p>
        </div>

        <div className="p-6 text-gray-800 space-y-6">
          <p className="text-sm">
            InMyNameKYC ("we", "us", or "our") is committed to protecting your
            privacy. This Privacy Policy explains how we collect, use, store,
            and protect your information when you use the InMyNameKYC mobile
            application (the "App").
          </p>
          <p className="text-sm">
            By using the App, you consent to the practices described in this
            policy.
          </p>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              1. Information We Collect
            </h2>
            <ul className="list-disc ml-6 mt-2 space-y-2">
              <li>
                <strong>a. Personal Identification Information</strong>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700 text-sm">
                  <li>Full name</li>
                  <li>Date of birth</li>
                  <li>Address</li>
                  <li>Contact details (e.g., email, phone number)</li>
                  <li>Nationality</li>
                </ul>
              </li>
              <li>
                <strong>b. Identity Verification Data</strong>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700 text-sm">
                  <li>Photo ID documents (e.g., passport, driving licence)</li>
                  <li>Selfie images (for facial matching)</li>
                  <li>
                    Document metadata (e.g., issuing country, expiry date)
                  </li>
                </ul>
              </li>
              <li>
                <strong>c. Device and Usage Information</strong>
                <ul className="list-disc ml-6 mt-1 space-y-1 text-gray-700 text-sm">
                  <li>Device model and operating system</li>
                  <li>IP address</li>
                  <li>
                    App usage activity (for troubleshooting and analytics)
                  </li>
                </ul>
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              2. How We Use Your Information
            </h2>
            <ul className="list-disc ml-6 space-y-2 text-sm">
              <li>
                Verify your identity for Know Your Customer (KYC) and Anti-Money
                Laundering (AML) compliance.
              </li>
              <li>Prevent fraud and unauthorised access.</li>
              <li>Provide support and troubleshoot technical issues.</li>
              <li>Improve and personalise the App experience.</li>
              <li>
                Comply with legal obligations and requests from law enforcement
                where applicable.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              3. Use of Camera and Biometrics
            </h2>
            <p>The App uses your device’s camera to:</p>
            <ul className="list-disc ml-6 space-y-2 mt-2 text-sm">
              <li>Capture images of your ID documents.</li>
              <li>
                Capture a live selfie to confirm liveness and perform biometric
                facial matching.
              </li>
            </ul>
            <p className="mt-2">
              These images are processed securely and only for the purpose of
              identity verification.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              4. Sharing Your Information
            </h2>
            <p>We may share your data with:</p>
            <ul className="list-disc ml-6 space-y-2 mt-2 text-sm">
              <li>
                Trusted identity verification providers for processing (e.g.,
                Entrust or equivalent partners)
              </li>
              <li>
                Regulatory or law enforcement agencies, if required by law
              </li>
              <li>
                Our secure cloud storage and analytics providers under strict
                data processing agreements
              </li>
            </ul>
            <p className="mt-2">
              We do not sell your personal data to third parties.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              5. Data Security
            </h2>
            <ul className="list-disc ml-6 space-y-2 mt-2 text-sm">
              <li>Encrypt your data during transmission and storage.</li>
              <li>Restrict access to authorised personnel only.</li>
              <li>
                Regularly monitor and test our systems for vulnerabilities.
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              6. Data Retention
            </h2>
            <p>Your data is retained only for as long as necessary to:</p>
            <ul className="list-disc ml-6 space-y-2 mt-2 text-sm">
              <li>Fulfil the KYC verification process.</li>
              <li>Meet legal and regulatory obligations.</li>
              <li>Support audit or dispute resolution requirements.</li>
            </ul>
            <p className="mt-2">
              After this period, data is securely deleted or anonymised.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              7. Your Rights
            </h2>
            <p>Depending on your jurisdiction, you may have rights to:</p>
            <ul className="list-disc ml-6 space-y-2 mt-2 text-sm">
              <li>Access a copy of your personal data.</li>
              <li>Correct or update inaccurate information.</li>
              <li>Request deletion of your data.</li>
              <li>Object to or restrict processing.</li>
            </ul>
            <p className="mt-2">
              To exercise your rights, please contact us using the details
              below.
            </p>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              8. Contact Us
            </h2>
            <ul className="list-disc ml-6 mt-2">
              <li>
                <strong>Email:</strong> support@inmynamekyc.co.uk
              </li>
              <li>
                <strong>Postal Address:</strong> Suite 1, Falstaff House, Enigma
                Commercial Centre, Enigma Business Park, Sandys Road, Malven,
                WR14 1JJ
              </li>
            </ul>
          </div>

          <div>
            <h2 className="text-xl font-semibold text-[#2e5b5c]">
              9. Updates to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. You will be
              notified via the App or email if material changes are made.
            </p>
          </div>
        </div>
        <div className="w-full p-5">
          <Link href="/welcome">
            <button
              type="button"
              className="w-full bg-[#4c7375] hover:bg-teal-700 rounded-md h-12 text-white font-medium shadow-md transition-colors duration-300"
            >
              Back
            </button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
